import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { colors, fonts, radii, breakpoints } from '../../../ui';

// Step 2 "Starting size" list (Figma session/10 middle: "Please select a starting logMAR";
// session/16 right: "Please select a starting text size"). The selected row is highlighted.

const Box = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  overflow: auto;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.button}px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
`;

const Option = styled.button`
  display: grid;
  grid-template-columns: ${({ cols }) => cols};
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0 20px;
  border: 0;
  border-bottom: 1px solid ${colors.slate[100]};
  border-left: 4px solid ${({ selected }) => (selected ? colors.primary : 'transparent')};
  background: ${({ selected }) => (selected ? colors.slate[200] : '#fff')};
  color: ${colors.slate[800]};
  font-family: ${fonts.family};
  font-size: 17px;
  font-weight: ${({ selected }) => (selected ? 700 : 400)};
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
  &:last-of-type {
    border-bottom: 0;
  }
  &:hover {
    background: ${({ selected }) => (selected ? colors.slate[200] : colors.slate[50])};
  }
  &:focus-visible {
    outline: 2px solid ${colors.brand[300]};
    outline-offset: -2px;
  }
  .muted {
    color: ${colors.slate[500]};
    font-size: 14px;
    font-weight: 400;
  }
  .chars {
    font-family: 'OpticianSans', ${fonts.family};
    letter-spacing: 0.9em;
    white-space: nowrap;
  }
  @media (max-width: ${breakpoints.phone - 1}px) {
    min-height: 44px;
    padding: 0 10px;
    font-size: 15px;
    .chars {
      letter-spacing: 0.5em;
    }
  }
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: ${({ cols }) => cols};
  gap: 8px;
  padding: 8px 20px 8px 24px;
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${colors.slate[50]};
  border-bottom: 1px solid ${colors.slate[200]};
  font-family: ${fonts.family};
  font-size: 12px;
  font-weight: 700;
  color: ${colors.slate[600]};
  text-align: center;
  @media (max-width: ${breakpoints.phone - 1}px) {
    padding: 6px 10px 6px 14px;
  }
`;

/**
 * @param rows      [{ id, cells: [node, ...] }]  first cell is the main value
 * @param header    optional column labels
 * @param selectedId, onSelect(row)
 * @param cols      CSS grid-template-columns for the cells
 * @param label     accessible name of the list
 */
export const StartSizeList = ({ rows, header, selectedId, onSelect, cols = '1fr', label = 'Starting size', mutedFrom = 99, charsCol = -1 }) => {
  const selectedRef = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    const box = boxRef.current;
    const row = selectedRef.current;
    if (!box || !row) return;
    const top = row.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    if (top < box.scrollTop || top + row.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = Math.max(0, top - box.clientHeight / 2);
    }
  }, [selectedId]);
  return (
    <Box role='listbox' aria-label={label} ref={boxRef}>
      {header && (
        <Head cols={cols} aria-hidden='true'>
          {header.map((h) => (
            <span key={h}>{h}</span>
          ))}
        </Head>
      )}
      {rows.map((row) => {
        const selected = row.id === selectedId;
        return (
          <Option
            key={row.id}
            type='button'
            role='option'
            aria-selected={selected}
            selected={selected}
            cols={cols}
            ref={selected ? selectedRef : null}
            onClick={() => onSelect(row)}
            data-testid={`start-size-${row.id}`}
          >
            {row.cells.map((c, i) => (
              <span key={i} className={i === charsCol ? 'chars' : i >= mutedFrom ? 'muted' : undefined}>
                {c}
              </span>
            ))}
          </Option>
        );
      })}
    </Box>
  );
};

export default StartSizeList;
